import { UserProduct } from '@interfaces/auth';
import { vibxConstants } from '@utils/constants';
import { Button } from '@vibTheme/components/_base/Button';
import { Link } from '@vibTheme/components/_base/Link';
import { Typography } from '@vibTheme/components/_base/Typography';
import { VibModal } from '@vibTheme/components/VibModal';
import { Dispatch, SetStateAction } from 'react';
import { Image } from '@vibTheme/components/_base/Image';
import { addGroupToDontShowGroupReconfigModal } from '@repository/dontShowGroupReconfigModal';

type Props = {
  group: UserProduct | null;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

export const GroupReconfigModal = (props: Props) => {
  const { group } = props;
  if (!group) return null;

  const resolveContent = () => {
    const modalContent = generateModalContent(group);
    const monitor = group.botMonitoring;

    if (monitor.leftOrKicked) return modalContent.leftOrKicked;
    if (monitor.withoutCommunication) return modalContent.withoutCommunication;
    if (monitor.withoutPermissions) return modalContent.withoutPermissions;

    return null;
  };
  const content = resolveContent();
  if (!content) return null;

  const closeModal = () => {
    props.setIsOpen(false);
  };
  const handleDontShowAgainClick = () => {
    addGroupToDontShowGroupReconfigModal(group.id);
    closeModal();
  };
  const hideReconfigButton =
    group.botMonitoring.withoutPermissions &&
    !group.botMonitoring.leftOrKicked &&
    !group.botMonitoring.withoutCommunication;

  return (
    <VibModal isOpen={props.isOpen} setIsOpen={props.setIsOpen}>
      <div className="md:p-main -mt-main">
        <div className="flex flex-col items-center gap-main md:flex-row md:gap-[5rem] pt-main md:pt-0">
          <div className="relative h-[150px] w-[150px] md:h-[250px] md:w-[250px]">
            <Image src="/assets/robot.svg" alt="Robozinho" fill />
          </div>

          <div className="max-w-[600px]">
            <Typography component="h1" className="pb-main">
              {content.title}
            </Typography>

            {content.content}

            <SupportFooter />
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-md pt-2xl justify-center md:justify-end">
          {!hideReconfigButton && (
            <Link
              href={`/products/${group.productCode}/basicSettings`}
              unstyled
            >
              <Button onClick={closeModal} className="w-full">
                Reconfigurar agora
              </Button>
            </Link>
          )}
          <Button variant="text" onClick={handleDontShowAgainClick}>
            Não mostrar novamente
          </Button>
        </div>
      </div>
    </VibModal>
  );
};

const SupportFooter = () => (
  <Typography>
    Caso precise de ajuda, fale com nosso suporte:
    <br />
    <Link href={vibxConstants.SUPPORT_TELEGRAM}>
      @atendimentovibx3bot
    </Link> ou <Link href={vibxConstants.SUPPORT_WHATSAPP}>WhatsApp</Link>
  </Typography>
);

const generateModalContent = (group: UserProduct) => ({
  withoutCommunication: {
    title: 'Problema de comunicação com o Bot',
    content: (
      <>
        <Typography>
          Parece que o seu bot da comunidade <strong>{group.name}</strong>{' '}
          perdeu o acesso ao grupo VIP ou pode ter sido excluído/banido pelo
          Telegram.
          <br />
          <br />
          Se o seu bot foi banido, você pode corrigir isso acessando a
          plataforma e substituindo o Token:
        </Typography>
        <ol className="ml-xl py-md marker:text-black">
          <li>
            <Typography>
              Vá até Menu &gt; Comunidades &gt; Selecione a Comunidade &gt;
              Configurações &gt; Alterar Token do Bot
            </Typography>
          </li>
          <li>
            <Typography>
              Gere um novo token no @BotFather e atualize na plataforma
            </Typography>
          </li>
        </ol>
      </>
    ),
  },
  leftOrKicked: {
    title: 'Bot sem acesso ao grupo ou Grupo inativo ou removido',
    content: (
      <>
        <Typography>
          O seu bot da comunidade <strong>{group.name}</strong> está com
          dificuldades para se comunicar com o grupo VIP. <br />
          Por favor, verifique se:
        </Typography>
        <ol className="ml-xl py-md marker:text-black">
          <li>
            <Typography>O bot ainda está dentro do grupo</Typography>
          </li>
          <li>
            <Typography>Ele tem permissões de administrador</Typography>
          </li>
        </ol>
        <Typography>
          Caso o grupo tenha sido removido ou banido pelo Telegram, você pode
          conectá-lo a um novo grupo:
        </Typography>
        <ol className="ml-xl py-md marker:text-black">
          <li>
            <Typography>Acesse a Área do Criador em app.vibx.com.br</Typography>
          </li>
          <li>
            <Typography>
              Vá em: Menu &gt; Comunidades &gt; [sua comunidade] &gt;
              Configurações &gt; Reconfigurar Grupo
            </Typography>
          </li>
        </ol>
      </>
    ),
  },
  withoutPermissions: {
    title: 'Bot sem permissão de administrador',
    content: (
      <>
        <Typography>
          O bot de vendas da comunidade <strong>{group.name}</strong> perdeu
          acesso de administrador no grupo VIP. Sem essa permissão, ele não
          consegue gerenciar os acessos dos assinantes.
        </Typography>
        <Typography className="py-md">
          Por favor, verifique as configurações do grupo e garanta que o bot
          esteja como administrador.
        </Typography>
      </>
    ),
  },
});
